import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Check, KeyRound } from 'lucide-react'
import {
    advanceWalletPasskeyStep,
    discardPendingWalletPasskey,
    loadWalletPasskeyStatus,
    passkeysUsableOnThisDevice,
    prepareWalletPasskeyAction,
    prepareWalletPasskeyEnrollment,
    type WalletPasskeyStatus,
    type WalletPasskeyStep,
    type WalletPasskeySummary,
} from 'lib/walletPasskeyRecovery'
import {
    passkeyBrowserUnavailableReason,
    walletPasskeyErrorMessage,
} from 'lib/walletPasskeyWebAuthn'
import styles from './wallet.module.scss'

const stepText = {
    create: ['Create passkey', 'Your browser will ask where to save a new passkey.'],
    encrypt: [
        'Protect backup',
        'Passkey created. Approve the same passkey again so it can encrypt a backup of both wallet keys.',
    ],
    verify: [
        'Verify recovery',
        'Approve your passkey again to check that the saved backup can be recovered.',
    ],
    recover: [
        'Unlock wallet',
        'Choose the passkey used for this backup to restore your wallet on this device.',
    ],
}

const setupSteps = [
    { kind: 'create', label: 'Create passkey' },
    { kind: 'encrypt', label: 'Encrypt backup with it' },
    { kind: 'verify', label: 'Confirm backup unlocks' },
] as const

const setupVerifyText =
    'Backup saved. Approve the same passkey one last time to confirm it unlocks the backup.'

export default function WalletPasskeyRecovery({
    userId,
    serverBackup = false,
}: {
    userId: string
    // Only accounts whose wallet Treechat also holds have a server backup to mention.
    serverBackup?: boolean
}) {
    const [status, setStatus] = useState<WalletPasskeyStatus | null>(null)
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [notice, setNotice] = useState('')
    const [stepKind, setStepKind] = useState<WalletPasskeyStep['kind'] | null>(null)
    const [recovered, setRecovered] = useState(false)
    // Pending plaintext is held only for enrollment, never in rendered state or
    // browser storage, and released on completion, cancellation, or timeout.
    const pending = useRef<WalletPasskeyStep | null>(null)
    const mounted = useRef(false)
    const inFlight = useRef(false)
    const unavailable = passkeyBrowserUnavailableReason()
    // Enrollment is three passkey prompts; a verify step belongs to it only
    // while the passkey is unverified (otherwise it is a standalone check).
    const pendingStep = pending.current
    const inSetup =
        stepKind === 'create' ||
        stepKind === 'encrypt' ||
        (pendingStep?.kind === 'verify' && !pendingStep.passkey.verified)
    const setupIndex = inSetup ? setupSteps.findIndex(step => step.kind === stepKind) : -1

    const reload = useCallback(async () => {
        const result = await loadWalletPasskeyStatus()
        if (mounted.current && result.identity.userId === userId) setStatus(result)
    }, [userId])

    useEffect(() => {
        mounted.current = true
        if (unavailable) {
            setLoading(false)
        } else {
            reload()
                .catch(err => {
                    if (mounted.current) setError(walletPasskeyErrorMessage(err))
                })
                .finally(() => {
                    if (mounted.current) setLoading(false)
                })
        }
        return () => {
            mounted.current = false
            pending.current = null
        }
    }, [reload, unavailable])

    useEffect(() => {
        if (!stepKind) return
        const timeout = window.setTimeout(() => {
            pending.current = null
            setStepKind(null)
            setNotice('Setup timed out. You can start again or finish a pending setup below.')
        }, 5 * 60 * 1000)
        return () => window.clearTimeout(timeout)
    }, [stepKind])

    const remember = (step?: WalletPasskeyStep) => {
        pending.current = step || null
        setStepKind(step?.kind || null)
    }

    const run = async (action: () => Promise<void>) => {
        if (inFlight.current) return
        inFlight.current = true
        setBusy(true)
        setError('')
        setNotice('')
        try {
            // Call immediately, before any await, to preserve the click's
            // activation for navigator.credentials.create/get.
            await action()
        } catch (err) {
            if (mounted.current) {
                remember()
                setError(walletPasskeyErrorMessage(err))
                await reload().catch(() => {})
            }
        } finally {
            inFlight.current = false
            if (mounted.current) setBusy(false)
        }
    }

    const prepare = (passkey: WalletPasskeySummary, recover: boolean) => {
        if (!status) return
        void run(async () => {
            const kind = recover ? 'recover' : passkey.envelope_present ? 'verify' : 'encrypt'
            const step = await prepareWalletPasskeyAction(passkey, status.identity, kind)
            if (mounted.current) remember(step)
        })
    }

    const advance = () => {
        const step = pending.current
        if (!step) return
        void run(async () => {
            const result = await advanceWalletPasskeyStep(step)
            if (!mounted.current) return
            remember(result.next)
            if (!result.next) {
                setRecovered(Boolean(result.recovered))
                setNotice(
                    result.recovered
                        ? 'Your wallet was recovered with its original payment and ordinal keys. Reload Treechat to use your wallet again.'
                        : 'Passkey recovery is verified for this wallet in this browser.',
                )
            }
            await reload()
        })
    }

    return (
        <section className={styles.selfCustodySetupCard} aria-label="Passkey wallet recovery">
            <div className={styles.selfCustodySetupHeader}>
                <KeyRound size={20} aria-hidden="true" />
                <div>
                    <div className={styles.selfCustodySetupTitle}>Passkey recovery</div>
                    <p className={styles.selfCustodySetupSubtitle}>
                        Recover your wallet if this browser loses its saved keys. Your passkey
                        protects an encrypted backup; normal payments need no new passkey prompt.
                    </p>
                </div>
            </div>

            {unavailable ? (
                <p className={styles.selfCustodySetupHint}>{unavailable}</p>
            ) : (
                <>
                    {loading && <p role="status">Loading recovery options…</p>}
                    {error && (
                        <p className={styles.selfCustodySetupError} role="alert">
                            {error}
                        </p>
                    )}
                    {notice && <p role="status">{notice}</p>}
                    {recovered && (
                        <button
                            className={styles.secondaryBtn}
                            onClick={() => window.location.reload()}
                        >
                            Reload Treechat
                        </button>
                    )}
                    {stepKind && (
                        <div>
                            {setupIndex >= 0 && (
                                <>
                                    <p className={styles.selfCustodySetupHint}>
                                        Step {setupIndex + 1} of {setupSteps.length}. Each step asks
                                        for the same passkey.
                                    </p>
                                    <ol className={styles.passkeySetupSteps}>
                                        {setupSteps.map((step, index) => (
                                            <li
                                                key={step.kind}
                                                className={
                                                    index < setupIndex
                                                        ? styles.passkeySetupStepDone
                                                        : index === setupIndex
                                                        ? styles.passkeySetupStepCurrent
                                                        : undefined
                                                }
                                                aria-current={
                                                    index === setupIndex ? 'step' : undefined
                                                }
                                            >
                                                <span className={styles.passkeySetupStepMarker}>
                                                    {index < setupIndex ? (
                                                        <Check size={12} aria-label="Done" />
                                                    ) : (
                                                        index + 1
                                                    )}
                                                </span>
                                                {step.label}
                                            </li>
                                        ))}
                                    </ol>
                                </>
                            )}
                            <p className={styles.selfCustodySetupHint}>
                                {stepKind === 'verify' && setupIndex >= 0
                                    ? setupVerifyText
                                    : stepText[stepKind][1]}
                            </p>
                            {stepKind !== 'create' && (
                                <p className={styles.selfCustodySetupHint}>
                                    Your phone will say “Sign in”. Approving only unlocks your
                                    wallet backup; it doesn’t sign you in or move funds.
                                </p>
                            )}
                            <div className={styles.selfCustodySetupActions}>
                                <button
                                    className={styles.secondaryBtn}
                                    disabled={busy}
                                    onClick={advance}
                                >
                                    {busy ? 'Please wait…' : stepText[stepKind][0]}
                                </button>
                                <button
                                    className={styles.secondaryBtn}
                                    disabled={busy}
                                    onClick={() => remember()}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                    {!stepKind && !recovered && (
                        <>
                            <div className={styles.selfCustodySetupActions}>
                                <button
                                    className={styles.secondaryBtn}
                                    disabled={busy || loading || !status}
                                    onClick={() =>
                                        void run(async () => {
                                            const step = await prepareWalletPasskeyEnrollment(
                                                status?.root_identity_key,
                                            )
                                            if (mounted.current) remember(step)
                                        })
                                    }
                                >
                                    {busy ? 'Please wait…' : 'Set up a recovery passkey'}
                                </button>
                                <button
                                    className={styles.secondaryBtn}
                                    disabled={busy || loading}
                                    onClick={() => void run(reload)}
                                >
                                    Refresh
                                </button>
                            </div>
                            <p className={styles.selfCustodySetupHint}>
                                Setup asks you to approve the same passkey three times in a row:
                                create it, encrypt your backup with it, then confirm the backup
                                unlocks.
                            </p>
                        </>
                    )}
                    {!stepKind &&
                        status &&
                        passkeysUsableOnThisDevice(status.passkeys).map((passkey, index) => (
                            <div key={passkey.id}>
                                <p className={styles.selfCustodySetupHint}>
                                    Passkey {index + 1} ·{' '}
                                    {new Date(passkey.created_at).toLocaleDateString()} ·{' '}
                                    {passkey.provider && `${passkey.provider.name} · `}
                                    {passkey.verified &&
                                    status?.root_identity_key &&
                                    passkey.root_identity_key !== status.root_identity_key
                                        ? 'Older backup without seed words. Set up a new passkey to include them'
                                        : passkey.verified
                                        ? 'Recovery verified'
                                        : `Setup incomplete (step ${
                                              passkey.envelope_present ? 3 : 2
                                          } of ${setupSteps.length})`}
                                </p>
                                <div className={styles.selfCustodySetupActions}>
                                    <button
                                        className={styles.secondaryBtn}
                                        disabled={busy || recovered}
                                        onClick={() => prepare(passkey, false)}
                                    >
                                        {passkey.verified ? 'Check recovery' : 'Finish setup'}
                                    </button>
                                    {passkey.verified && (
                                        <button
                                            className={styles.secondaryBtn}
                                            disabled={busy || recovered}
                                            onClick={() => prepare(passkey, true)}
                                        >
                                            Recover on this device
                                        </button>
                                    )}
                                    {!passkey.verified && (
                                        <button
                                            className={styles.secondaryBtn}
                                            disabled={busy || recovered}
                                            onClick={() => {
                                                if (
                                                    !window.confirm(
                                                        'Discard this unfinished passkey setup? You can create a new recovery passkey afterwards.',
                                                    )
                                                )
                                                    return
                                                void run(async () => {
                                                    await discardPendingWalletPasskey(passkey.id)
                                                    await reload()
                                                })
                                            }}
                                        >
                                            Discard setup
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    <p className={styles.selfCustodySetupHint}>
                        Keep a downloaded wallet backup too. Recovery on another phone requires the
                        same passkey and a compatible browser and password manager.
                        {serverBackup &&
                            ' Treechat’s existing server backup remains in place during this pilot.'}
                    </p>
                </>
            )}
        </section>
    )
}
